import classNames from 'classnames';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { Button as CustomButton } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { InfoCircle } from '../../icons/InfoCircle/InfoCircle';
import { OverlaySpinner } from '../../components/OverlaySpinner/OverlaySpinner';
import { Status, ValidationErrors } from '../../types';
import { Text } from '../../components/Text/Text';
import { useStore, INSURANCE_EXCLUDED_DISPLAY_LABELS } from '../../store';
import * as api from '../../services/api';
import styles from './QuotesComparisons.module.scss';
import { formatMoney, track } from '../../util';
import { BRAND_COLORS } from '../../config';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faHeartPulse, faStethoscope, faTooth, faFlask, faComputer, faPrint, faTv } from '@fortawesome/free-solid-svg-icons';
import { Form } from 'react-bootstrap';
import ReactStars from 'react-stars'
import ProgressBar from 'react-bootstrap/ProgressBar';

const ReactStarsComponent = ReactStars as React.ComponentType<any>;

const COURIER_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 23, 24, 25, 26, 27, 28];

const TSS_SPECIALIZED_TYPES = [
  { icon: faHeartPulse,   label: 'Healthcare solutions' },
  { icon: faStethoscope,  label: 'Medical equipment' },
  { icon: faTooth,        label: 'Dental equipment' },
  { icon: faFlask,        label: 'Scientific & laboratory equipment' },
  { icon: faComputer,     label: 'IT power and technology' },
  { icon: faPrint,        label: 'Large printers' },
  { icon: faTv,           label: 'Exhibitions, conferences and trade shows' },
];

type Props = {
  className?: string;
};
export function QuotesComparisons(props: Props) {
  const m = useLocation();
  const orderId = useStore((s) => s.orderId);
  const pickupSuburb = useStore((s) => s.pickupSuburb);
  const pickupState = useStore((s) => s.pickupState);
  const pickupPostcode = useStore((s) => s.pickupPostcode);
  const destinationSuburb = useStore((s) => s.destinationSuburb);
  const destinationState = useStore((s) => s.destinationState);
  const destinationPostcode = useStore((s) => s.destinationPostcode);
  const customOrderName = useStore((s) => s.customOrderName);
  const customOrderPhone = useStore((s) => s.customOrderPhone);
  const customOrderEmail = useStore((s) => s.customOrderEmail);
  const customOrder = useStore((s) => s.customOrder);
  const items = useStore((s) => s.items);
  const quotes = useStore((s) => s.quotes);
  const quoteId = useStore((s) => s.quoteId);
  const courierServiceName = useStore((s) => s.courierServiceName);
  const currentStep = useStore((s) => s.currentStep);
  const reCreateOrder = useStore((state) => state.reCreateOrder);
  const progressBarPercentage = useStore((state) => state.progressBarPercentage);
  const dropOffPOBox = useStore((state) => state.dropOffPOBox);
  const hideInsuranceFields = useStore((state) => state.hideInsuranceFields);
  const earliestCollectionDay = useStore((state) => state.earliestCollectionDay);
  const setQuoteId = useStore((s) => s.setQuoteId);
  const setProgressBarPercentage = useStore((s) => s.setProgressBarPercentage);
  const setQuotes = useStore((s) => s.setQuotes);
  const setCurrentStep = useStore((s) => s.setCurrentStep);
  const lockOfficialQuote = useStore((s) => s.lockOfficialQuote);
  const setQuotePrice = useStore((s) => s.setQuotePrice);
  const setQuoteIsAuthorityToLeave = useStore((s) => s.setQuoteIsAuthorityToLeave);
  const setReQuoteMessage = useStore((s) => s.setReQuoteMessage);
  const setQuoteInsurance = useStore((s) => s.setQuoteInsurance);
  const setQuoteDelivery = useStore((s) => s.setQuoteDelivery);
  const setNoteTitle = useStore((s) => s.setNoteTitle);
  const setNoteSubTitle = useStore((s) => s.setNoteSubTitle);
  const setNoteBody = useStore((s) => s.setNoteBody);
  const setCourierServiceName = useStore((s) => s.setCourierServiceName);
  const setOrderId = useStore((state) => state.setOrderId);
  const setReCreateOrder = useStore((state) => state.setReCreateOrder);
  const setQuotePickupCutOffTime = useStore((state) => state.setQuotePickupCutOffTime);
  const setCustomOrderName = useStore((state) => state.setCustomOrderName);
  const setCustomOrderPhone = useStore((state) => state.setCustomOrderPhone);
  const setCustomOrderEmail = useStore((state) => state.setCustomOrderEmail);
  const setCustomOrder = useStore((state) => state.setCustomOrder);
  const pushQuotes = useStore((state) => state.pushQuotes);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [showNoWarranty, setShowNoWarranty] = useState(false);
  const handleCloseNoWarranty = () => setShowNoWarranty(false);
  const handleShowNoWarranty = () => setShowNoWarranty(true);
  // Generic courier notice modal state
  const [activeNotice, setActiveNotice] = useState<{ body: string; requires_consent: boolean; consent_text: string | null; single_button?: boolean } | null>(null);
  const [noticeConsent, setNoticeConsent] = useState(false);
  const handleNoticeModalClose = () => { setActiveNotice(null); setNoticeConsent(false); };
  const history = useHistory();
  const [status, setStatus] = useState<Status>('idle');
  // Number of /quote requests that have settled (resolved or failed). The
  // "couldn't find a quote" fallback keys off this rather than the progress
  // bar, which is only a visual approximation and reaches 100% early.
  const [completedQuoteRequests, setCompletedQuoteRequests] = useState(0);
  const [savingStatus, setSavingStatus] = useState<Status>('idle');
  const addInsurance = useStore((state) => state.addInsurance);
  const currentDay = new Date().getDay();
  const setQuoteExcludingGstPrice = useStore((s) => s.setQuoteExcludingGstPrice);
  const [showAEThirdPartyInfoModal, setShowAEThirdPartyInfoModal] = useState(false);
  const handleCloseAEThirdPartyInfoModal = () => setShowAEThirdPartyInfoModal(false);
  const handleShowAEThirdPartyInfoModal = () => setShowAEThirdPartyInfoModal(true);
  const [tssModalShow, setTSSModalShow] = useState(false);
  const [tssModalConsent, setTSSModalConsent] = useState(false);
  const [tssConsentText, setTssConsentText] = useState('');
  const handleTSSModalClose = () => {
    setTSSModalShow(false);
    setTSSModalConsent(false);
  };
  const modalStyle: CSSProperties & Record<'--bs-modal-width', string> = {
    '--bs-modal-width': 'min(840px, calc(100vw - 24px))',
  };
  const modalHeaderStyle = {
    alignItems: 'center',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 24px',
  } as const;
  const modalTitleStyle = {
    color: '#121a33',
    fontSize: '20px',
    fontWeight: 600,
    letterSpacing: '0.005em',
    lineHeight: 1.1,
    margin: 0,
  } as const;
  const modalBodyStyle = {
    color: '#1f2937',
    fontSize: '14px',
    lineHeight: 1.35,
    padding: '20px 24px 12px',
  } as const;
  const consentRowStyle = { display: 'flex', alignItems: 'flex-start' } as const;
  const consentLabelStyle = {
    alignItems: 'flex-start',
    cursor: 'pointer',
    display: 'flex',
    gap: '10px',
    lineHeight: 1.45,
    margin: 0,
    maxWidth: '100%',
  } as const;
  const consentInputStyle = {
    height: '1px',
    left: '-9999px',
    opacity: 0,
    position: 'absolute',
    width: '1px',
  } as const;
  const getConsentIndicatorStyle = (checked: boolean) => ({
    alignItems: 'center',
    backgroundColor: checked ? BRAND_COLORS.primary : BRAND_COLORS.white,
    border: `2px solid ${checked ? BRAND_COLORS.primary : '#9ca3af'}`,
    borderRadius: '4px',
    color: '#ffffff',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 700,
    height: '18px',
    justifyContent: 'center',
    lineHeight: 1,
    marginTop: '2px',
    width: '18px',
  });
  const consentTextStyle = { color: '#1f2937', lineHeight: 1.45, margin: 0 } as const;
  const modalFooterStyle = {
    background: '#f8fafc',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-end',
    padding: '10px 24px 14px',
    width: '100%',
  } as const;
  const secondaryButtonStyle = {
    alignItems: 'center',
    backgroundColor: BRAND_COLORS.primary,
    border: 0,
    borderRadius: '9px',
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    fontWeight: 600,
    fontSize: '14px',
    height: '42px',
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: 'clamp(150px, 40vw, 185px)',
    opacity: 1,
    padding: '8px 18px',
    whiteSpace: 'nowrap',
  } as const;
  const getProceedButtonStyle = (enabled: boolean) => ({
    alignItems: 'center',
    backgroundColor: enabled ? BRAND_COLORS.primary : '#f7be95',
    border: 0,
    borderRadius: '9px',
    color: '#fff',
    cursor: enabled ? 'pointer' : 'not-allowed',
    display: 'inline-flex',
    fontSize: '14px',
    fontWeight: 600,
    height: '42px',
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: 'clamp(100px, 28vw, 110px)',
    opacity: enabled ? 1 : 0.9,
    padding: '8px 16px',
    whiteSpace: 'nowrap',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showQuotes, setShowQuotes] = useState(false);
  const [sortMode, setSortMode] = useState<'price' | 'delivery' | 'rating' | ''>('');
  const setPickupSuburb = useStore((state) => state.setPickupSuburb);
  const setPickupPostcode = useStore((state) => state.setPickupPostcode);
  const setPickupState = useStore((state) => state.setPickupState);
  const setPickupBuildingType = useStore((state) => state.setPickupBuildingType);
  const setPickupTailLift = useStore((state) => state.setPickupTailLift);
  const setDestinationSuburb = useStore((state) => state.setDestinationSuburb);
  const setDestinationPostcode = useStore((state) => state.setDestinationPostcode);
  const setDestinationState = useStore((state) => state.setDestinationState);
  const setDestinationBuildingType = useStore((state) => state.setDestinationBuildingType);
  const setDropOffTailLift = useStore((state) => state.setDropOffTailLift);
  const setDropOffPOBox = useStore((state) => state.setDropOffPOBox);
  const addItem = useStore((state) => state.addItem);
  const editItem = useStore((state) => state.editItem);
  const setEarliestCollectionDay = useStore((state) => state.setEarliestCollectionDay);
  const setServiceName = useStore((state) => state.setServiceName);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function renderConsentRow(
    id: string,
    checked: boolean,
    onToggle: () => void,
    consentContent?: React.ReactNode
  ) {
    return (
      <div style={consentRowStyle}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          style={consentInputStyle}
        />
        <label htmlFor={id} style={consentLabelStyle}>
          <span style={getConsentIndicatorStyle(checked)}>{checked ? '✓' : ''}</span>
          <span style={consentTextStyle}>
            {consentContent}
          </span>
        </label>
      </div>
    );
  }
  
  useEffect(() => {
    (async () => {
      if (!orderId) {
        const payload = new URLSearchParams(m.search).get('payload');
        if (payload) {
          setStatus('in-progress');
          let widgetData: any = {};
          try {
            widgetData = JSON.parse(atob(payload!));
            setPickupSuburb(widgetData.pickupSuburb);
            setPickupState(widgetData.pickupState);
            setPickupPostcode(widgetData.pickupPostcode);
            setPickupBuildingType(widgetData.pickupBuildingType);
            setPickupTailLift(widgetData.pickupTailLift);
            setDestinationSuburb(widgetData.destinationSuburb);
            setDestinationPostcode(widgetData.destinationPostcode);
            setDestinationState(widgetData.destinationState);
            setDestinationBuildingType(widgetData.destinationBuildingType);
            setDropOffTailLift(widgetData.dropOffTailLift);
            setDropOffPOBox(widgetData.dropOffPOBox);
            setOrderId(widgetData.orderId);
            widgetData.items.forEach((item: any, index: any) => {
              if (index >= 1) {
                addItem();
              }
              editItem(index, 'type', item.type);
              editItem(index, 'weight', item.weight);
              editItem(index, 'length', item.length);
              editItem(index, 'width', item.width);
              editItem(index, 'height', item.height);
              editItem(index, 'quantity', item.quantity);
              editItem(index, 'contents', item.contents);
              editItem(index, 'calculatedLength', item.length);
              editItem(index, 'calculatedWidth', item.width);
              editItem(index, 'calculatedHeight', item.height);
            });
            setStatus('idle');
          } catch (e) { }
        }
      }

      const elm = document.querySelector<HTMLElement>('.progress-bar')!;
      const elm2 = document.querySelector<HTMLElement>('.progress')!;
      const loadingLabel = document.querySelector<HTMLElement>('.loading-label')!;
      elm.style.backgroundColor = '#ffb570';
      if (progressBarPercentage >= 100) {
        setTimeout(() => {
          elm2.style.display = 'none';
          loadingLabel.style.display = 'none';
        }, 1000);
      }
      if (customOrder) {
        return;
      }
      if (status !== 'idle') {
        return;
      }
      if ((quotes.length === 0 || currentStep === 0) && (orderId)) {
        try {
          setQuotes([]);
          setCompletedQuoteRequests(0);
          setStatus('in-progress');
          const urls = COURIER_IDS.map(id => `/quote/${orderId}/${id}`);

          urls.forEach(url => {
            callAPI(url)
          });

        } catch (e: any) {
          track('app-error-quote-retrieval', { orderId, error: e?.response?.data });
        }
      }
    })();
  }, [
    status,
    history,
    orderId,
    pickupSuburb,
    pickupPostcode,
    destinationSuburb,
    destinationPostcode,
    items,
    pickupState,
    destinationState,
    progressBarPercentage,
  ]);

  useEffect(() => {
    if (currentStep === 2) {
      setShowQuotes(true);
      const elm2 = document.querySelector<HTMLElement>('.progress')!;
      const loadingLabel = document.querySelector<HTMLElement>('.loading-label')!;
      if (progressBarPercentage >= 100) {
        elm2.style.display = 'none';
        loadingLabel.style.display = 'none';
      }
    } else {
      setTimeout(() => {
        setProgressBarPercentage(5);
        setShowQuotes(true);
      }, 1000);
    }
    // Official Quote bookings are locked to shipment-details; bounce back-nav here.
    if (lockOfficialQuote) {
      history.replace('/shipment-details');
      return;
    }
    const payload = new URLSearchParams(m.search).get('payload');
    if (!orderId && !payload) {
      history.push('/package-details');
      return;
    }
    setCurrentStep(1);
  }, [history, orderId, setCurrentStep, lockOfficialQuote]);

  // Drive the progress bar from the number of settled requests — success,
  // failure and timeout all count — so it tracks the courier list exactly and
  // reaches 100% only once every request is in.
  //
  // Computed from the count rather than accumulated per response: repeatedly
  // adding 100/26 lands on 99.99999999999994, which never satisfies the
  // `>= 100` checks the loading label and Stepper rely on.
  //
  // Deliberately not gated on `status`: it flips to 'complete' as soon as the
  // first quote arrives, which would freeze the bar at the first response.
  useEffect(() => {
    const percentage = Math.round((completedQuoteRequests / COURIER_IDS.length) * 100);
    setProgressBarPercentage(Math.max(5, percentage));
  }, [completedQuoteRequests, setProgressBarPercentage]);

  useEffect(() => {
    if (quotes.length > 0) {
      setStatus('complete');
      return;
    }
    // Only declare "no quotes" once every courier request has actually settled.
    // Couriers are queried in COURIER_IDS order and slower ones (TSS is last)
    // can still be in flight after the earlier ones have returned empty.
    // The status check keeps a stale counter from a previous search out of the
    // verdict: setQuotes([]) and setCompletedQuoteRequests(0) are dispatched
    // from an async block, which React 17 does not batch, so they land on
    // separate renders.
    if (status === 'in-progress' && completedQuoteRequests >= COURIER_IDS.length) {
      setStatus('complete');
      setCustomOrder(true);
    }
  }, [quotes, completedQuoteRequests, status, setCustomOrder]);

  const isSameDayDeliveryQuote = (q: any) => {
    const courier = typeof q === 'string' ? q : q?.courierName;
    const eta = typeof q === 'object' && typeof q?.eta === 'string' ? q.eta : '';
    if (['Direct Couriers', 'Bonds Couriers', 'Zoom2u', 'Felix Transport', 'Josies Transport', 'Capital Transport'].includes(courier)) {
      return true;
    }
    if (courier === 'BSH Logistics') {
      return eta.toLowerCase().includes('same day');
    }
    return false;
  };

  function getNextMonday(quote: any) {
    if (isSameDayDeliveryQuote(quote)) {
      return <div><div><FontAwesomeIcon icon={faBolt} color={BRAND_COLORS.primary} /><span style={{ fontStyle: 'italic', fontSize: '13px', color: BRAND_COLORS.primary }}>{' Same Day Delivery'}</span></div>{moment().startOf('isoWeek').add(1, 'week').format('dddd Do')}</div>;
    } else {
      return moment().startOf('isoWeek').add(1, 'week').format('dddd Do');
    }
  }

  async function callAPI(url: any) {
    try{
        const timeoutEnv = process.env.REACT_APP_QUOTES_API_TIMEOUT_MS;
        const timeout = timeoutEnv ? parseInt(timeoutEnv, 10) : 15000;
        const newDataArr: any = await api.get<any>(url, { timeout });
      
        if(newDataArr.data?.length >0 && newDataArr.data[0].courierName === 'MRL Global'){
          setEarliestCollectionDay(newDataArr.data[0].selectedDays);
        }
        pushQuotes(newDataArr.data, false);
    } catch (e: any) {
      pushQuotes([], false);
      track('app-error-quote-retrieval', { orderId, error: e?.response?.data });
    } finally {
      setCompletedQuoteRequests((count) => count + 1);
    }
  }

  async function save(quoteId: string, courierServiceName = '', quotePickupCutOffTime = '', serviceName: string, courierId?: number, notice?: any) {
    setQuoteId(quoteId);
    setCourierServiceName(courierServiceName);
    setQuotePickupCutOffTime(quotePickupCutOffTime);
    setServiceName(serviceName);

    // Show courier notice modal if this courier has one (from API response)
    if (notice) {
      if (courierId === 28) { // TSS has custom card-grid layout
        setTssConsentText(notice.consent_text || '');
        return setTSSModalShow(true);
      }
      return setActiveNotice(notice);
    }

    try {
      let hashId: any;
      hashId = orderId;
      setSavingStatus('in-progress');
      if (reCreateOrder) {
        const reCreateOrderRecord = await api.post<any>(`/order/${orderId}/reCreateOrder`, {});
        hashId = reCreateOrderRecord.id;
        setOrderId(reCreateOrderRecord.id);
        setReCreateOrder(false);
      }
      await api.csrf();
      const response = await api.patch(`/order/${hashId}/select-quote`, { quoteId }) as any;
      const { data } = response;
      
      setQuotePrice(data.priceExcludingGst);
      setQuoteExcludingGstPrice(data.priceExcludingGst);
      setQuoteIsAuthorityToLeave(
        typeof data.isAuthorityToLeave === 'boolean' ? data.isAuthorityToLeave : null
      );
      setReQuoteMessage(null);
      setNoteTitle(data.noteTitle);
      setNoteSubTitle(data.noteSubTitle);
      setNoteBody(data.noteBody);
      setQuoteDelivery(data.eta);
      setQuoteInsurance(data.insuranceCategory);
      addInsurance(data.insurance);
      track('app-select-quote', {
        priceExcludingGst: data.priceExcludingGst,
        priceIncludingGst: data.priceIncludingGst,
        insuranceCategory: data.insuranceCategory,
        courierServiceName: courierServiceName,
      });

      let url = `/shipment-details?courierService=${courierServiceName}`;

      history.push(url);
    } catch (e) {
      setSavingStatus('error');
    }
  }

  function getEtaInfo(eta: string | undefined) {
    if (!eta) {
      return { minDays: Number.MAX_SAFE_INTEGER, exactDays: null as number | null, isExact: false };
    }

    const normalizedEta = eta.toLowerCase().trim();

    if (normalizedEta.includes('same day')) {
      return { minDays: 0, exactDays: 0, isExact: true };
    }

    if (normalizedEta.includes('overnight') || normalizedEta.includes('next day')) {
      return { minDays: 1, exactDays: 1, isExact: true };
    }

    const isRange = /\d+\s*-\s*\d+/.test(normalizedEta) || /\d+\s*to\s*\d+/.test(normalizedEta);
    const exactMatch = normalizedEta.match(/^(\d+)\s*(business\s*)?days?$/);
    const firstNumberMatch = normalizedEta.match(/\d+/);
    const minDays = firstNumberMatch ? parseInt(firstNumberMatch[0], 10) : Number.MAX_SAFE_INTEGER;

    if (isRange) {
      return { minDays, exactDays: null, isExact: false };
    }

    if (exactMatch) {
      return { minDays, exactDays: minDays, isExact: true };
    }

    return { minDays, exactDays: null, isExact: false };
  }

  const cheapestQuoteId = useMemo(() => {
    if (quotes.length === 0) {
      return '';
    }

    return quotes.reduce((lowest, quote) =>
      quote.priceIncludingGst < lowest.priceIncludingGst ? quote : lowest
    ).id;
  }, [quotes]);

  const fastestQuoteIds = useMemo(() => {
    if (quotes.length === 0) {
      return new Set<string>();
    }

    const exactEtaQuotes = quotes.filter((quote) => getEtaInfo(quote.eta).isExact);

    if (exactEtaQuotes.length === 0) {
      return new Set<string>();
    }

    const fastestExactDays = Math.min(
      ...exactEtaQuotes.map((quote) => getEtaInfo(quote.eta).exactDays ?? Number.MAX_SAFE_INTEGER)
    );

    return new Set(
      exactEtaQuotes
        .filter((quote) => (getEtaInfo(quote.eta).exactDays ?? Number.MAX_SAFE_INTEGER) === fastestExactDays)
        .map((quote) => quote.id)
    );
  }, [quotes]);

  const topRatedQuoteIds = useMemo(() => {
    if (quotes.length === 0) {
      return new Set<string>();
    }

    const highestRating = Math.max(...quotes.map((quote) => quote.rating || 0));

    if (highestRating <= 0) {
      return new Set<string>();
    }

    return new Set(quotes.filter((quote) => (quote.rating || 0) === highestRating).map((quote) => quote.id));
  }, [quotes]);

  const sortedQuotes = useMemo(() => {
    const nextQuotes = [...quotes];

    if (sortMode === 'delivery') {
      return nextQuotes.sort((a, b) => {
        const etaA = getEtaInfo(a.eta);
        const etaB = getEtaInfo(b.eta);

        if (etaA.isExact !== etaB.isExact) {
          return etaA.isExact ? -1 : 1;
        }

        const daysA = etaA.exactDays ?? etaA.minDays;
        const daysB = etaB.exactDays ?? etaB.minDays;

        return daysA - daysB;
      });
    }

    if (sortMode === 'rating') {
      return nextQuotes.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }

    return nextQuotes.sort((a, b) => {
      const aSameDay = getEtaInfo(a.eta).minDays === 0;
      const bSameDay = getEtaInfo(b.eta).minDays === 0;
      if (aSameDay !== bSameDay) return aSameDay ? -1 : 1;
      return a.priceIncludingGst - b.priceIncludingGst;
    });
  }, [quotes, sortMode]);

  async function saveAEHunterQuote() {
    try {
      let hashId: any;
      hashId = orderId;
      setSavingStatus('in-progress');
      if (reCreateOrder) {
        const reCreateOrderRecord = await api.post<any>(`/order/${orderId}/reCreateOrder`, {});
        hashId = reCreateOrderRecord.id;
        setOrderId(reCreateOrderRecord.id);
        setReCreateOrder(false);
      }
      await api.csrf();
      const response = await api.patch(`/order/${hashId}/select-quote`, { quoteId }) as any;
      const { data } = response;
      
      setQuotePrice(data.priceExcludingGst);
      setQuoteExcludingGstPrice(data.priceExcludingGst);
      setQuoteIsAuthorityToLeave(
        typeof data.isAuthorityToLeave === 'boolean' ? data.isAuthorityToLeave : null
      );
      setReQuoteMessage(null);
      setNoteTitle(data.noteTitle);
      setNoteSubTitle(data.noteSubTitle);
      setNoteBody(data.noteBody);
      setQuoteDelivery(data.eta);
      setQuoteInsurance(data.insuranceCategory);
      addInsurance(data.insurance);
      track('app-select-quote', {
        priceExcludingGst: data.priceExcludingGst,
        priceIncludingGst: data.priceIncludingGst,
        insuranceCategory: data.insuranceCategory,
        courierServiceName: courierServiceName,
      });
      history.push('/shipment-details?courierService=' + courierServiceName);
    } catch (e) {
      setSavingStatus('error');
    }
  }

  async function saveCustomQuote() {
    setCourierServiceName('Custom Quote');
    try {
      let hashId: any;
      hashId = orderId;
      setSavingStatus('in-progress');
      await api.csrf();
      const response = await api.patch(`/order/${hashId}/custom-quote`, {
        name: customOrderName,
        phone: customOrderPhone,
        email: customOrderEmail,
      }) as any;
      const { data } = response;
      addInsurance(data.insurance);
      track('app-custom-order', {
        name: customOrderName,
        email: customOrderEmail,
        phone: customOrderPhone,
        referenceID: hashId,
      });
      history.push('/shipment-details?courierService=' + courierServiceName);
    } catch (e: any) {
      setSavingStatus('error');
      setErrors(e?.response?.data?.errors ?? {});
    }
  }

  function getNextSelectableDay(date: any, selectableDays: any) {
    let nextDay = new Date(date);
    while (!selectableDays.includes(days[nextDay.getDay()])) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    return nextDay;
  }

  function getCollectionAvailabilityText(quote: any, earliestCollectionDay: any, currentDay: any) {
    if (quote.courierName === 'MRL Global' && earliestCollectionDay && earliestCollectionDay[0] !== "Daily") {
      if (earliestCollectionDay.length === 1) {
        if (moment().isAfter(moment(quote.pickupCutOffTime, 'HH:mm'))) {
          return "Tomorrow";
        } else {
          return "Collection Available Today";
        }
      } else if (earliestCollectionDay.length > 0) {
        let today = new Date();
        let todayDayName = days[today.getDay()];
  
        let nextSelectableDates: string[] = [];
        for (let i = 0; i < earliestCollectionDay.length; i++) {
          let day = earliestCollectionDay[i];
          if (day === todayDayName && moment().isAfter(moment(quote.pickupCutOffTime, 'HH:mm'))) {
            nextSelectableDates.push("Next " + day);
          } else {
            nextSelectableDates.push(day);
          }
        }
    
        // Filter out duplicates and sort by the days' order
        nextSelectableDates = Array.from(new Set(nextSelectableDates));
        nextSelectableDates.sort((a, b) => days.indexOf(a.replace("Next ", "")) - days.indexOf(b.replace("Next ", "")));
    
        return nextSelectableDates.join(' and ');
      }
    }

    const isSameDay = isSameDayDeliveryQuote(quote);

    if (
      (quote.courierName === 'TNT' ||
        quote.courierName === 'Toll' ||
        quote.courierName === 'Star Track' ||
        quote.courierName === 'Couriers Please' ||
        quote.courierName === 'Toll Palletised Express' ||
        quote.courierName === 'Northline' ||
        quote.courierName === 'Northline Express' ||
        quote.courierName === 'CTI Logistics Regional Freight' ||
        quote.courierName === 'MRL Global' ||
        (quote.courierName === 'BSH Logistics' && !isSameDay)) &&
      currentDay !== 6 &&
      currentDay !== 0 &&
      !moment().isAfter(moment(quote.pickupCutOffTime, 'HH:mm'))
    ) {
      return 'Collection Available Today';
    }

    if (isSameDay && currentDay !== 6 && currentDay !== 0 && !moment().isAfter(moment(quote.pickupCutOffTime, 'HH:mm'))) {
      return (
        <div>
          <div>
            <FontAwesomeIcon icon={faBolt} color={BRAND_COLORS.primary} />
            <span style={{ fontStyle: 'italic', fontSize: '13px', color: BRAND_COLORS.primary }}>{' Same Day Delivery'}</span>
          </div>
          {'Collection Available Today'}
        </div>
      );
    }

    if (currentDay === 5 || currentDay === 6) {
      return getNextMonday(quote);
    }

    if (isSameDay) {
      return (
        <div>
          <div>
            <FontAwesomeIcon icon={faBolt} color={BRAND_COLORS.primary} />
            <span style={{ fontStyle: 'italic', fontSize: '13px', color: BRAND_COLORS.primary }}>{' Same Day Delivery'}</span>
          </div>
          {'Tomorrow'}
        </div>
      );
    }

    return 'Tomorrow';
  }

  const repeatQuoteSearch = () => {
      history.push('/package-details');
  }

  type WarningModalConfig = {
    title?: string;
    body: React.ReactNode;
    consent?: {
      checked: boolean;
      content?: React.ReactNode;
      id: string;
      onToggle: () => void;
    };
    hideConsentHeader?: boolean;
    onHide: () => void;
    onProceed: () => void;
    show: boolean;
    // Purely informational notices (e.g. Zoom2u's service description) show only
    // the Proceed button - there's nothing to consent to or decline.
    hideCancelButton?: boolean;
  };

  function renderWarningModal(config: WarningModalConfig) {
    const consent = config.consent;
    const hasConsent = !!consent;
    const canProceed = consent?.checked ?? true;

    return (
      <Modal
        show={config.show}
        onHide={config.onHide}
        animation={true}
        aria-labelledby="contained-modal-title-vcenter"
        style={modalStyle}
        dialogClassName={styles.quoteConsentModalDialog}
      >
        <Modal.Header closeButton style={modalHeaderStyle}>
          <Modal.Title style={modalTitleStyle}>{config.title || 'NOTICE'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={modalBodyStyle}>
          {config.body}
          {hasConsent && consent && (
            <>
              {!config.hideConsentHeader && (
                <>
                  <br />
                  <br />
                  To proceed, please consent to the below
                  <br />
                  <br />
                </>
              )}
              {config.hideConsentHeader && <div style={{ marginTop: '16px' }} />}
              {renderConsentRow(consent.id, consent.checked, consent.onToggle, consent.content)}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={modalFooterStyle}>
          <button
            type="button"
            onClick={config.onProceed}
            style={getProceedButtonStyle(canProceed)}
            disabled={!canProceed}
          >
            Proceed
          </button>
          {!config.hideCancelButton && (
            <button type="button" onClick={config.onHide} style={secondaryButtonStyle}>
              Choose Different Carrier
            </button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
  
  return (
    <div className={styles.root}>
      <Header />
      <OverlaySpinner
        message={status === 'in-progress' ? 'Searching our couriers to find you the best quotes' : ''}
        enabled={savingStatus === 'in-progress' || status === 'in-progress'}
      />
      <ProgressBar style={{ height: "0.5rem", marginTop: "10px" }} now={progressBarPercentage ? progressBarPercentage : 0} />
      <span className="loading-label" style={{ fontSize: '14px' }}>Loading...</span>
      {customOrder || status === 'error' ? (
        <div>
          {dropOffPOBox ? (
            <div className='text-center pt-5'>
              <b>We see that you have selected a PO BOX as a destination address</b>
              <Text>
                <p className='pt-2'>Please note that PO BOX deliveries are limited, <a href="#" onClick={repeatQuoteSearch}>click here</a> to search again without a PO BOX delivery.</p>
              </Text>
            </div>
          ) : (
            <div>
              <div className='text-center pt-5'>
              <b>Sorry we couldn't find a quote</b>
              <Text>
                <p className='pt-2'>Please go back to the package details page and check your dimensions to make sure you have the right measurements in centimetres or millimetres.</p>
              </Text>
              <Text>
                <p className='pt-2'>If measurements look ok and we still can't retrieve a quote for you then please submit the form below.</p>
              </Text>
            </div>
              <div className={styles.mainDiv}>
                <div className={styles.customQuote}>
                  <div className={styles.customQuoteBlock}>
                    <div>
                      <div style={{ paddingTop: '15px' }}>
                        <span className={styles.labelHeading}>Name *</span>
                        <Form>
                          <Form.Group controlId="exampleForm.ControlTextarea1">
                            <Form.Control
                              type="text"
                              placeholder="Name"
                              name="customOrderName"
                              value={customOrderName}
                              onChange={(e) => {
                                setCustomOrderName(e.target.value || '');
                              }}
                              className={styles.input}
                            />
                          </Form.Group>
                          <span className={styles.error}>{errors.name ? errors.name : ''}</span>
                        </Form>
                      </div>
                      <div>
                        <span className={styles.labelHeading}>Email *</span>
                        <Form>
                          <Form.Group controlId="exampleForm.ControlTextarea1">
                            <Form.Control
                              type="text"
                              placeholder="Email"
                              name="customOrderEmail"
                              value={customOrderEmail}
                              onChange={(e) => {
                                setCustomOrderEmail(e.target.value || '');
                              }}
                              className={styles.input}
                            />
                          </Form.Group>
                          <span className={styles.error}>{errors.email ? errors.email : ''}</span>
                        </Form>
                      </div>
                      <div>
                        <span className={styles.labelHeading}>Phone *</span>
                        <Form>
                          <Form.Group controlId="exampleForm.ControlTextarea1">
                            <Form.Control
                              type="text"
                              placeholder="Phone"
                              name="customOrderPhone"
                              value={customOrderPhone}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (!isNaN(+value)) {
                                  setCustomOrderPhone(e.target.value || '');
                                } else {
                                  setCustomOrderPhone(customOrderPhone);
                                }
                              }}
                              className={styles.input}
                            />
                          </Form.Group>
                          <span className={styles.error}>{errors.phone ? errors.phone : ''}</span>
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.toolbar}>
                <Button type="submit" onClick={() => {
                  saveCustomQuote();
                }} className={styles.customOrderSubmitBtn}>
                  Submit
                </Button>
              </div>
            </div>
          )}


        </div>
      ) : (
        <div className={styles.table}>
          <Modal
            show={show}
            onHide={handleClose}
            animation={true}
            aria-labelledby="transit-warranty-modal-title"
            centered
          >
            <Modal.Header closeButton style={{ borderBottom: `3px solid ${BRAND_COLORS.primary}`, padding: '20px 24px 16px' }}>
              <Modal.Title
                id="transit-warranty-modal-title"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#1a202c',
                  letterSpacing: '0.01em',
                }}
              >
                <span style={{ color: BRAND_COLORS.primary, display: 'inline-flex', flexShrink: 0 }}><InfoCircle /></span>
                Transit Warranty
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#2d3748', marginBottom: '14px' }}>
                Your shipment is covered by a{' '}
                <strong style={{ color: BRAND_COLORS.primary, fontWeight: 600 }}>complementary transit warranty up to $450</strong>.
                Additional protection can be purchased at checkout.
              </p>
              <div style={{
                backgroundColor: '#fffaf0',
                border: '1px solid #feebc8',
                borderLeft: `4px solid ${BRAND_COLORS.primary}`,
                borderRadius: '4px',
                padding: '12px 16px',
                fontSize: '0.8125rem',
                lineHeight: 1.55,
                color: '#744210',
              }}>
                For full coverage details, please review the{' '}
                <a href="https://fastcourier.com.au/extended-liability-terms-conditions" target="_blank" rel="noreferrer" style={{ color: BRAND_COLORS.primary, fontWeight: 600, textDecoration: 'underline' }}>
                  Extended Liability Warranty page
                </a>.
              </div>
            </Modal.Body>
            <Modal.Footer style={{ padding: '12px 24px 20px', borderTop: '1px solid #edf2f7' }}>
              <Button onClick={handleClose} className="border-0" style={{ backgroundColor: BRAND_COLORS.primary, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: '0.02em', padding: '8px 24px' }}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showNoWarranty}
            onHide={handleCloseNoWarranty}
            animation={true}
            aria-labelledby="no-warranty-modal-title"
            centered
          >
            <Modal.Header closeButton style={{ borderBottom: `3px solid ${BRAND_COLORS.primary}`, padding: '20px 24px 16px' }}>
              <Modal.Title
                id="no-warranty-modal-title"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#1a202c',
                  letterSpacing: '0.01em',
                }}
              >
                <span style={{ color: BRAND_COLORS.primary, display: 'inline-flex', flexShrink: 0 }}><InfoCircle /></span>
                No Warranty Available
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#2d3748', marginBottom: '14px' }}>
                Transit warranty coverage is{' '}
                <strong style={{ color: BRAND_COLORS.primary, fontWeight: 600 }}>not available</strong>{' '}
                for the selected item type. The following categories are excluded:
              </p>
              <ul style={{ paddingLeft: '18px', marginBottom: 0, color: '#4a5568', fontSize: '0.9375rem', lineHeight: 1.9 }}>
                {Object.values(INSURANCE_EXCLUDED_DISPLAY_LABELS).map((label) => (
                  <li key={label} style={{ marginBottom: '2px' }}>{label}</li>
                ))}
              </ul>
            </Modal.Body>
            <Modal.Footer style={{ padding: '12px 24px 20px', borderTop: '1px solid #edf2f7' }}>
              <Button onClick={handleCloseNoWarranty} className="border-0" style={{ backgroundColor: BRAND_COLORS.primary, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: '0.02em', padding: '8px 24px' }}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showAEThirdPartyInfoModal}
            onHide={handleCloseAEThirdPartyInfoModal}
            animation={true}
            aria-labelledby="contained-modal-title-vcenter"
            centered
          >
            <Modal.Header
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Modal.Title>NOTICE</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              The package will be collected/delivered by a third party courier managed by Allied Express.
              <br />
              <br />
              Please note third party pick ups / deliveries may be subject to delays.
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={handleCloseAEThirdPartyInfoModal} className="border-0" style={{ backgroundColor: BRAND_COLORS.primaryHover }}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
          <div className={styles.quoteToolbar}>
            
            <div className={styles.sortControls}>
              <button
                type="button"
                className={classNames(styles.sortButton, { [styles.sortButtonActive]: sortMode === 'price' })}
                onClick={() => setSortMode('price')}
              >
                Lowest Price
              </button>
              <button
                type="button"
                className={classNames(styles.sortButton, { [styles.sortButtonActive]: sortMode === 'delivery' })}
                onClick={() => setSortMode('delivery')}
              >
                Fastest
              </button>
              <button
                type="button"
                className={classNames(styles.sortButton, { [styles.sortButtonActive]: sortMode === 'rating' })}
                onClick={() => setSortMode('rating')}
              >
                Top Rated
              </button>
            </div>
          </div>

          <div className={classNames(styles.head, styles.headWithInsurance)}>
            <div className={classNames(styles.headingCell)}>
              <Text weight="700">Carrier</Text>
            </div>
            <div className={classNames(styles.headingCell)}>
              <Text weight="700">Service</Text>
              <Text size="extraSmall" color="grey600" className={styles.smallHeading}>
                Estimated Delivery Time - Business days ONLY
              </Text>
            </div>
            <div className={classNames(styles.headingCell, styles.centerText)}>
              <Text weight="700">Price</Text>
              <Text size="extraSmall" color="grey600" className={styles.smallHeading}>
                (Inc. GST)
              </Text>
            </div>
            <div className={classNames(styles.headingCell, styles.centerText)}>
              <Text weight="700">Transit Warranty</Text>
            </div>
            <div className={classNames(styles.headingCell, styles.centerText)}>
              <Text weight="700">Earliest collection date</Text>
            </div>
            <div className={classNames(styles.headingCell)}></div>
          </div>

          {sortedQuotes.map((quote) => (
            <div key={quote.id} style={{ display: showQuotes ? 'block' : 'none' }}>
              {(() => {
                const hasBestPriceBadge = quote.id === cheapestQuoteId;
                const hasFastestBadge = fastestQuoteIds.has(quote.id);
                const hasTopRatedBadge = topRatedQuoteIds.has(quote.id);
                const hasAnyBadge = hasBestPriceBadge || hasFastestBadge || hasTopRatedBadge;
                const normalizedCourierName = quote.courierName.toLowerCase();
                const isMyPostQuote = normalizedCourierName.includes('my post') || normalizedCourierName.includes('eparcel');

                return (
              <div
                className={classNames(
                  styles.row,
                  styles.rowWithInsurance,
                  {
                    [styles.selected]: quote.id === quoteId,
                    [styles.bestPriceRow]: quote.id === cheapestQuoteId,
                    [styles.myPostRow]: isMyPostQuote,
                  }
                )}
              >
                <div className={styles.leftCol}>
                  <div className={classNames(styles.cell)} data-label="Carrier">
                    <img className={styles.logo} alt={quote.courierName || quote.name} src={quote.logo} />
                  </div>
                  <div className={classNames(styles.cell, styles.collectionCell)} data-label="Earliest collection">
                    {getCollectionAvailabilityText(quote, earliestCollectionDay, currentDay)}
                  </div>
                  <div className={classNames(styles.cell, styles.priceCenter, styles.centerText)} data-label="Price">
                    <p className={styles.priceTag}>{formatMoney(quote.priceIncludingGst)}</p>
                    <p className={styles.gstTag}>Inc. GST</p>
                    {quote.isEstimatedPrice && (
                      <p style={{ fontSize: '11px', color: '#6c757d', margin: '4px 0 0 0', display: 'block' }}>
                        Smart price{' '}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`estimated-price-tooltip-${quote.id}`}>
                              The final price may change based on the street address provided in the next step.
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <InfoCircle className={styles.icon} />
                          </span>
                        </OverlayTrigger>
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.rightCol}>
                  <div className={classNames(styles.cell, styles.serviceCell)} data-label="Service">
                    <div className={styles.serviceTitleRow}>
                      <Text family="brand" weight="700" className={styles.serviceName}>
                        {quote.name}
                        {quote.name === 'Allied Express Via Third Party Courier' && (
                          <span style={{ display: "inline-block" }} onClick={handleShowAEThirdPartyInfoModal}>
                            <InfoCircle className={styles.icon} />
                          </span>
                        )}
                      </Text>
                      {quote.courierName === 'TSS Sensitive Freight' && (
                        <div className={styles.tssTooltipWrap}>
                          <span className={classNames(styles.badge, styles.badgeTssSpecialist)}>
                            Sensitive Freight Specialist
                          </span>
                          <div className={styles.tssTooltip}>
                            <div className={styles.tssTooltipTitle}>Service Inclusions</div>
                            <ul className={styles.tssTooltipList}>
                              {TSS_SPECIALIZED_TYPES.map((t, i) => (
                                <li key={i} className={styles.tssTooltipItem}>
                                  <FontAwesomeIcon icon={t.icon} className={styles.tssTooltipIcon} />
                                  <span>{t.label}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      {hasAnyBadge && (
                      <div className={styles.badges}>
                        {hasBestPriceBadge && (
                          <span className={classNames(styles.badge, styles.badgeBestPrice)}>Best Price</span>
                        )}
                        {hasFastestBadge && (
                          <span className={classNames(styles.badge, styles.badgeFastest)}>Fastest</span>
                        )}
                        {hasTopRatedBadge && (
                          <span className={classNames(styles.badge, styles.badgeTopRated)}>Top Rated</span>
                        )}
                      </div>
                      )}
                    </div>
                    {quote.subLabel && (
                      <Text className={styles.subLabel} color="grey600" weight="400" size="extraSmall">
                        {quote.subLabel}
                      </Text>
                    )}
                    <Text size="small" className={styles.serviceEta}>{quote.eta}</Text>
                    <div className={styles.ratingWrap}>
                      <ReactStarsComponent
                        count={5}
                        value={quote.rating}
                        half={true}
                        size={20}
                        edit={false}
                        color2={BRAND_COLORS.primary}
                      />
                    </div>
                  </div>
                  <div className={classNames(styles.cell, styles.compText)} data-label="Transit Warranty">
                    {hideInsuranceFields ? (
                      <div className={styles.info}>
                        <span className={styles.infoIconTrigger} onClick={handleShowNoWarranty}>
                          <InfoCircle className={styles.icon} />
                        </span>
                        <span className={styles.noWarranty} onClick={handleShowNoWarranty}>
                          No warranty
                        </span>
                      </div>
                    ) : (
                      quote.insuranceCategory && (
                        <div className={styles.info}>
                          <span className={styles.infoIconTrigger} onClick={handleShow}>
                            <InfoCircle className={styles.icon} />
                          </span>
                          <a className={styles.warrantyLink} href={quote.insurance_link} target="_blank" rel="noreferrer">
                            Complementary cover<br /> up to $450
                          </a>
                        </div>
                      )
                    )}
                  </div>
                </div>
                <div className={classNames(styles.cell, styles.actionCell)} data-label="Action">
                  <CustomButton
                    className={styles.buyButton}
                    size="medium"
                    onClick={() =>
                      save(quote.id, quote.courierName, quote.pickupCutOffTime, quote.name, quote.courierId, quote.notice)
                    }
                  >
                    Book Now
                  </CustomButton>
                </div>
              </div>
              );
              })()}
            </div>
          ))}
          {/* Generic courier notice modal — driven by API quote response */}
          {activeNotice && (() => {
            const notice = activeNotice;
            return renderWarningModal({
              show: true,
              onHide: handleNoticeModalClose,
              onProceed: () => {
                handleNoticeModalClose();
                saveAEHunterQuote();
              },
              hideCancelButton: !!notice.single_button,
              ...(notice.requires_consent
                ? {
                    consent: {
                      id: 'courier-notice-modal-consent',
                      checked: noticeConsent,
                      onToggle: () => setNoticeConsent(!noticeConsent),
                      content: notice.consent_text
                        ? <span dangerouslySetInnerHTML={{ __html: notice.consent_text }} />
                        : undefined,
                    },
                  }
                : {}),
              body: <span dangerouslySetInnerHTML={{ __html: notice.body }} />,
            });
          })()}
          {renderWarningModal({
            show: tssModalShow,
            title: 'TSS Sensitive Freight - Transport Requirements',
            hideConsentHeader: true,
            onHide: handleTSSModalClose,
            onProceed: () => {
              handleTSSModalClose();
              saveAEHunterQuote();
            },
            consent: {
              id: 'tss-modal-consent',
              checked: tssModalConsent,
              onToggle: () => setTSSModalConsent(!tssModalConsent),
              content: <span dangerouslySetInnerHTML={{ __html: tssConsentText }} />,
            },
            body: (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#111827', fontWeight: 700, fontSize: '17px', marginBottom: '6px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND_COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    <span>Service Overview</span>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '15px', lineHeight: '1.5' }}>
                    TSS Sensitive Freight specializes in high-care, end-to-end transport for delicate, high-value, and sensitive commercial equipment.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                  <div style={{ backgroundColor: '#ebfbf2', border: '1px dashed #6ee7b7', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 700, fontSize: '16px', marginBottom: '16px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#10b981">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span>Accepted Goods</span>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {[
                        'Commercial Printers',
                        'IT Power & Tech Infrastructure',
                        'Gaming & Vending Machines',
                        'Medical & Healthcare Equipment',
                        'Commercial Electronics',
                        'Bulky Specialized Items',
                      ].map((item, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#4b5563', fontSize: '15px', marginBottom: idx === 5 ? 0 : '10px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{ backgroundColor: '#fff5f5', border: '1px dashed #fca5a5', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontWeight: 700, fontSize: '16px', marginBottom: '16px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#ef4444">
                        <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                      </svg>
                      <span>Restricted Items & Exclusions</span>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {[
                        'Hazardous or Dangerous Goods',
                        'Unpackaged Machinery or Parts',
                        'Items at Risk of Leaking Oils/Fluids',
                        'Improperly Secured Loose Goods',
                      ].map((item, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#4b5563', fontSize: '15px', marginBottom: idx === 3 ? 0 : '10px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            ),
          })}
        </div>
      )}
    </div>
  );
}
